import type { CSSProperties, ReactNode } from 'react'
import { useId } from 'react'
import { AlertTriangle } from 'lucide-react'
import styles from './TrustGauge.module.css'

export interface TrustGaugeProps {
  /** Trust score on the 0–100 display scale. */
  score: number
  /** Diameter in pixels. Default 40. */
  size?: number
  /** Hide the centered number, showing the ring only. Default false. */
  hideNumber?: boolean
  /** Accessible label / tooltip. Defaults to "Trust score: {score}/100". */
  title?: string
  /**
   * Override the gradient stops. By default the ring uses the trust ramp
   * (pink → yellow → blue → green as the score rises). Pass an explicit gradient
   * to invert it — e.g. a smurf-risk gauge where a high score is bad (green → red).
   */
  gradient?: { from: string; to: string }
  /**
   * A threshold this score has to clear — a season's minimum trust, typically.
   * Below it the ring turns danger-coloured and carries a warning glyph,
   * because the number alone does not tell you the roster is ineligible.
   */
  minRequired?: number | null
  /**
   * Shown in the centre instead of the number — an avatar initial, say, when
   * the ring wraps a person. The accessible label still carries the score.
   */
  children?: ReactNode
  className?: string
}

/** Gradient stops keyed to the trust-score thresholds used across the platform. */
function gaugeGradient(score: number): { from: string; to: string } {
  if (score >= 80) return { from: 'var(--nexus-green)', to: 'var(--nexus-blue)' }
  if (score >= 50) return { from: 'var(--nexus-blue)', to: 'var(--nexus-purple)' }
  if (score >= 20) return { from: 'var(--nexus-yellow)', to: 'var(--nexus-pink)' }
  return { from: 'var(--nexus-pink)', to: 'var(--nexus-pink-active)' }
}

/**
 * Compact circular trust-score gauge — a gradient ring with the score at its
 * center. The gradient shifts with the score band (pink → yellow → blue → green).
 */
export function TrustGauge({
  score,
  size = 40,
  hideNumber = false,
  title,
  gradient,
  minRequired,
  children,
  className,
}: TrustGaugeProps) {
  const gradientId = useId()
  const value = Math.max(0, Math.min(100, Math.round(score)))
  const radius = 16
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (value / 100) * circumference
  const belowMin = minRequired != null && value < minRequired
  const { from, to } = gradient
    ?? (belowMin ? { from: 'var(--nexus-pink)', to: 'var(--nexus-pink-active)' } : gaugeGradient(value))
  // The number grows with the ring so a 112px hero and a 24px chip both read.
  const fontSize = Math.max(10, Math.round(size * 0.28))
  const label = title ?? (belowMin
    ? `Trust score: ${value}/100 — below the minimum of ${minRequired}`
    : `Trust score: ${value}/100`)

  return (
    <div
      className={`${styles.gauge}${className ? ` ${className}` : ''}`}
      // The arc's three dynamic values ride here as custom properties and reach
      // the <circle> by inheritance, rather than as an inline style on the
      // circle itself. React writes inline styles through `element.style`, and
      // a browser with SVG turned off (Firefox's `svg.disabled`, set by several
      // privacy-hardening profiles) hands back a plain Element with no `.style`
      // — React throws mid-render and the root error boundary blanks the page.
      // This wrapper is a <div>, so it always has one.
      style={{
        width: size,
        height: size,
        '--gauge-stroke': `url(#${gradientId})`,
        '--gauge-dasharray': circumference,
        '--gauge-dashoffset': offset,
      } as CSSProperties}
      role="img"
      aria-label={label}
      title={label}
    >
      <svg className={styles.ring} viewBox="0 0 40 40" width={size} height={size}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>
        <circle className={styles.track} cx="20" cy="20" r={radius} />
        <circle className={styles.progress} cx="20" cy="20" r={radius} />
      </svg>
      {children != null ? (
        <span className={styles.inner} style={{ fontSize }}>{children}</span>
      ) : !hideNumber && (
        <span className={styles.number} style={{ color: from, fontSize }}>
          {value}
        </span>
      )}
      {belowMin && <AlertTriangle className={styles.warning} size={10} aria-hidden="true" />}
    </div>
  )
}
